import {
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import {
  getOrganizationEmailTemplates,
  getOrganizationId,
} from 'src/services/user.service';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { secondaryFont } from 'src/theme/typography';
import Scrollbar from 'src/components/scrollbar';
import {
  bulkCandidateAction,
  bulkCandidateEmail,
} from 'src/services/candidate.service';
import Animation from 'src/assets/animations/components/animation-wrapper';
import FormProvider from 'src/components/hook-form/form-provider';
import * as Yup from 'yup';
import { RHFTextField } from 'src/components/hook-form';
import RHFTinyEditor from 'src/components/hook-form/rhf-tiny-editor';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';

export default function EmailPreview({
  open,
  onClose,
  type,
  CANDIDATE_ACTIONS_MAP,
  JOB_DETAILS_TABS,
  noOfCandidates,
  checkCandidates,
  currentTab,
  checkAllCandidates,
  jobId,
  setCheckedCandidates,
  setCheckAllCandidates,
  setShowSelectionToolbar,
  setShowNavbar,
  refetchCandidates,
}) {
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);
  const router = useRouter();

  // states
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // variables
  const isCustomEmail = type === CANDIDATE_ACTIONS_MAP.CUSTOM_EMAIL.value;

  const defaultValues = useMemo(
    () => ({
      subject: '',
      description: '',
    }),
    [],
  );

  const schema = Yup.object().shape({
    subject: Yup.string().required('Subject is required'),
    description: Yup.string().required('Email Description is required'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  // functions
  const getBgColor = () => {
    if (CANDIDATE_ACTIONS_MAP[type].type === 'success') {
      return 'primaryGreen2';
    }
    if (CANDIDATE_ACTIONS_MAP[type].type === 'info') {
      return 'primaryGrey1';
    }
    return 'primaryRed2';
  };

  const getColor = () => {
    if (CANDIDATE_ACTIONS_MAP[type].type === 'success') {
      return 'primaryGreen1';
    }
    if (CANDIDATE_ACTIONS_MAP[type].type === 'info') {
      return 'primaryBlack2';
    }
    return 'primaryRed1';
  };

  const getBody = htmlString => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const divContent = doc.querySelector('body > div');

    if (divContent) {
      return divContent.innerHTML;
    }

    return doc.querySelector('body').innerHTML;
  };

  const onSend = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        request_type: CANDIDATE_ACTIONS_MAP[type].value,
        candidate_group_type: currentTab,
      };

      if (checkAllCandidates) {
        payload.apply_on_all = true;
      } else {
        payload.candidate_ids = checkCandidates.map(item => item.id);
        payload.apply_on_all = false;
      }

      const response = await bulkCandidateAction(
        organization_id,
        jobId,
        payload,
      );

      if (response.success) {
        setIsSubmitting(false);
        setIsSubmitted(true);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to send email', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCustomEmail = handleSubmit(async data => {
    try {
      setIsSubmitting(true);
      const payload = {
        candidate_group_type: currentTab,
        subject: data.subject,
        email_body: data.description,
      };

      if (checkAllCandidates) {
        payload.apply_on_all = true;
      } else {
        payload.candidate_ids = checkCandidates.map(item => item.id);
        payload.apply_on_all = false;
      }

      const response = await bulkCandidateEmail(
        organization_id,
        jobId,
        payload,
      );

      if (response.success) {
        setIsSubmitting(false);
        setIsSubmitted(true);
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to send email', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const LoadingMessage = (
    <Stack
      spacing="75px"
      justifyContent="center"
      alignItems="center"
      height="100%"
    >
      <Animation
        width={305}
        height={305}
        animationData={CANDIDATE_ACTIONS_MAP[type].loading.animation}
      />
      <Stack spacing="12px" justifyContent="center" alignItems="center">
        <Typography variant="h3">
          {CANDIDATE_ACTIONS_MAP[type].loading.title}
        </Typography>
        <Typography
          variant="input"
          color={theme => theme.palette.content.primaryBlack3}
        >
          {CANDIDATE_ACTIONS_MAP[type].loading.message}
        </Typography>
      </Stack>
    </Stack>
  );

  const SuccessMessage = (
    <Stack
      spacing="32px"
      justifyContent="center"
      alignItems="center"
      height="100%"
    >
      <Animation
        animationData={CANDIDATE_ACTIONS_MAP[type].success.animation}
      />
      <Stack spacing="12px" justifyContent="center" alignItems="center">
        <Typography variant="h3">
          {CANDIDATE_ACTIONS_MAP[type].success.title}
        </Typography>
        <Typography
          variant="input"
          color={theme => theme.palette.content.primaryBlack3}
        >
          {CANDIDATE_ACTIONS_MAP[type].success.message.replace(
            '{tab-name}',
            JOB_DETAILS_TABS[currentTab].label,
          )}
        </Typography>
      </Stack>
    </Stack>
  );

  // effects
  useEffect(() => {
    getOrganizationEmailTemplates(organization_id)
      .then(res => {
        setEmailTemplate(
          res.body.email_templates?.[CANDIDATE_ACTIONS_MAP[type].emailTemplate],
        );
      })
      .catch(err => {
        console.log(err);
      });
  }, [CANDIDATE_ACTIONS_MAP, organization_id, type]);

  // if there are errors show snackbar
  useEffect(() => {
    if (errors.subject || errors.description) {
      const errorMsg = errors.subject?.message || errors.description?.message;
      enqueueSnackbar(errorMsg, {
        variant: 'error',
      });
    }
  }, [errors]);

  return (
    <FormProvider methods={methods} onSubmit={handleCustomEmail}>
      <Dialog open={open} onClose={onClose} maxWidth>
        <DialogContent
          sx={{
            px: '20px',
            py: '24px',
            width: '773px',
            height: '533px',
          }}
        >
          {!isSubmitting && !isSubmitted && (
            <Stack spacing="20px">
              <Stack
                px="16px"
                py="11.5px"
                bgcolor={theme => theme.palette.background[getBgColor()]}
                color={theme => theme.palette.content[getColor()]}
                borderRadius="6px"
              >
                <Typography fontWeight={500}>
                  {CANDIDATE_ACTIONS_MAP[type].message.replace(
                    '{no-of-candidates}',
                    `${
                      noOfCandidates === 1
                        ? `${noOfCandidates} candidate`
                        : `${noOfCandidates} candidates`
                    }`,
                  )}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing="12px">
                <Iconify width={32} icon="tabler:mail-share" />
                <Typography variant="h3">
                  {CANDIDATE_ACTIONS_MAP[type].title}
                </Typography>
              </Stack>
              <Stack spacing="8px">
                <Typography variant="subheading2">Subject</Typography>
                {isCustomEmail ? (
                  <RHFTextField name="subject" />
                ) : (
                  <Typography variant="input">
                    {emailTemplate?.email_subject}
                  </Typography>
                )}
              </Stack>
              <Stack spacing="8px">
                <Typography variant="subheading2">Body</Typography>
                {isCustomEmail ? (
                  <RHFTinyEditor name="description" />
                ) : (
                  emailTemplate?.email_body && (
                    <Box
                      sx={{
                        borderRadius: '6px',
                        border: theme =>
                          `1px solid ${theme.palette.border.primaryBlack3}`,
                        maxWidth: '733px',
                        padding: '20px 12px 18px 12px',
                        maxHeight: '342px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Scrollbar
                        sx={{
                          maxHeight: '304px',
                          maxWidth: '690px',
                          width: '690px',
                        }}
                      >
                        <Typography
                          variant="input"
                          color={theme => theme.palette.content.primaryBlack2}
                          dangerouslySetInnerHTML={{
                            __html: getBody(emailTemplate?.email_body),
                          }}
                        />
                      </Scrollbar>
                    </Box>
                  )
                )}
              </Stack>
              {!isCustomEmail && (
                <Stack direction="row" alignItems="center" spacing="4px">
                  <Typography fontWeight={500} fontFamily={secondaryFont}>
                    To change the above content. Go back to the
                  </Typography>
                  <Typography
                    onClick={() => router.replace(paths.dashboard.message.root)}
                    fontFamily={secondaryFont}
                    fontWeight={500}
                    color={theme => theme.palette.content.accent1}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    Automated email
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
          {isSubmitting && !isSubmitted && LoadingMessage}
          {isSubmitted && !isSubmitting && SuccessMessage}
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            alignItems="center"
            spacing="12px"
            width="100%"
          >
            {isSubmitted && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  refetchCandidates();
                  setCheckAllCandidates(false);
                  setCheckedCandidates([]);
                  setShowSelectionToolbar(false);
                  setShowNavbar(true);
                  onClose();
                }}
              >
                Done
              </Button>
            )}
            {!isSubmitting && !isSubmitted && (
              <>
                <Button
                  sx={{
                    bgcolor: theme => theme.palette.background.primaryGrey2,
                    color: theme => theme.palette.content.primaryBlack3,
                  }}
                  variant="contained"
                  fullWidth
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type={isCustomEmail ? 'submit' : 'button'}
                  variant="contained"
                  fullWidth
                  onClick={() =>
                    isCustomEmail ? handleCustomEmail() : onSend()
                  }
                >
                  Send
                </Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}

EmailPreview.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  type: PropTypes.string,
  CANDIDATE_ACTIONS_MAP: PropTypes.object,
  JOB_DETAILS_TABS: PropTypes.object,
  noOfCandidates: PropTypes.number,
  checkCandidates: PropTypes.array,
  currentTab: PropTypes.string,
  checkAllCandidates: PropTypes.bool,
  jobId: PropTypes.string,
  setCheckedCandidates: PropTypes.func,
  setCheckAllCandidates: PropTypes.func,
  setShowSelectionToolbar: PropTypes.func,
  refetchCandidates: PropTypes.func,
  setShowNavbar: PropTypes.func,
};
